/**
 * Month Wise Table Component
 * Displays payment history showing which installment months were paid in each payment month
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';

/**
 * Month Wise Table Component
 * X-axis: Installment months (JUL25, AUG25, OCT25, etc.) - which term the payment was for
 * Y-axis: Payment months (Nov 25, Dec 25, etc.) - when the payment was actually made
 * Values: Amount paid in that payment month for that specific installment term
 * 
 * @param {Object} studentData - Student data
 * @param {Object} feeHistoryData - Fee history data from API (contains monthWiseData array)
 * @param {Boolean} isLoading - Loading state
 * @returns {JSX.Element} - Month Wise table component
 */
export const MonthWiseTable = ({ studentData, feeHistoryData = null, isLoading = false }) => {
    const monthWiseDataFromAPI = feeHistoryData?.monthWiseData || { headers: [], data: [] };
    
    const monthColumns = monthWiseDataFromAPI.headers || [];
    const monthWiseData = monthWiseDataFromAPI.data || [];

    const classTotals = {};
    monthColumns.forEach(col => {
        const total = monthWiseData.reduce((sum, record) => sum + (record[col.field] || 0), 0);
        classTotals[col.field] = total;
    });

    const totalBalance = {};
    monthColumns.forEach(col => {
        const columnSum = monthWiseData.reduce((sum, record) => sum + (record[col.field] || 0), 0);
        totalBalance[col.field] = columnSum - col.total;
    });

    const amountBodyTemplate = (value) => {
        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#1F2937',
                fontSize: '0.9375rem'
            }}>
                {value.toFixed(2)}
            </span>
        );
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    // Show empty table with basic columns if no month columns exist
    if (!monthColumns || monthColumns.length === 0) {
        return (
            <div className="tab-content feetype-wise-content">
                <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                    <DataTable
                        value={[]}
                        className="fee-details-datatable"
                        showGridlines
                        stripedRows
                        tableStyle={{ tableLayout: 'fixed', width: '100%' }}
                        emptyMessage={
                            <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#6B7280',
                                fontSize: '0.875rem',
                                fontFamily: 'InterRegular'
                            }}>
                                No month wise data available
                            </div>
                        }
                    >
                        <Column
                            field="sNo"
                            header="S.NO"
                            style={{ width: '100px', textAlign: 'center' }}
                            headerStyle={{ width: '100px' }}
                        />
                        <Column
                            field="paidMonth"
                            header="PAID MONTH"
                            style={{ width: '150px' }}
                            headerStyle={{ width: '150px' }}
                        />
                    </DataTable>
                </div>
            </div>
        );
    }

    return (
        <div className="tab-content feetype-wise-content">
            <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                <DataTable
                    value={monthWiseData.length > 0 ? monthWiseData : []}
                    className="fee-details-datatable"
                    showGridlines
                    stripedRows
                    tableStyle={{ tableLayout: 'fixed', width: '100%' }}
                    emptyMessage={
                        <div style={{
                            textAlign: 'center',
                            color: '#6B7280',
                            fontSize: '0.875rem',
                            fontFamily: 'InterRegular'
                        }}>
                            No payment records available yet
                        </div>
                    }
                >
                    <Column
                        field="sNo"
                        header="S.NO"
                        style={{ width: '100px', textAlign: 'center' }}
                        headerStyle={{ width: '100px' }}
                    />
                    <Column
                        field="paidMonth"
                        header="PAID MONTH"
                        style={{ width: '150px' }}
                        headerStyle={{ width: '150px' }}
                    />
                    {monthColumns.map((col) => (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={`${col.label} (${col.total.toFixed(2)})`}
                            body={(rowData) => amountBodyTemplate(rowData[col.field] || 0)}
                            style={{ width: `${100 / monthColumns.length}%` }}
                            headerStyle={{ width: `${100 / monthColumns.length}%` }}
                        />
                    ))}
                </DataTable>

                <DataTable
                    value={[{
                        label: 'Total Balance:',
                        ...totalBalance
                    }]}
                    className="fee-totals-table"
                    showGridlines
                    tableStyle={{ tableLayout: 'fixed', width: '100%' }}
                >
                    <Column
                        body={(rowData) => <strong style={{ color: '#DC2626', fontFamily: 'InterSemiBold' }}>{rowData.label}</strong>}
                        style={{ width: '250px', backgroundColor: '#FFFFFF' }}
                        headerStyle={{ width: '250px' }}
                    />
                    {monthColumns.map((col) => (
                        <Column
                            key={col.field}
                            body={(rowData) => <strong style={{ color: '#DC2626', fontFamily: 'InterSemiBold' }}>{Math.abs(rowData[col.field]).toFixed(2)}</strong>}
                            style={{ width: `${100 / monthColumns.length}%`, backgroundColor: '#FFFFFF' }}
                            headerStyle={{ width: `${100 / monthColumns.length}%` }}
                        />
                    ))}
                </DataTable>
            </div>
        </div>
    );
};
